import React, { useRef, useState } from "react";
import { Form, Button, FormControl, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ColorPicker from "../../../lib/plugins/color-picker/ColorPicker";

const DETAIL_ROW_DEFAULTS = {
  segments: [],
  separator: " · ",
  hideEmpty: true,
  style: "chips",
  font: { fontFamily: "Inter", fontSize: 7, color: "#6B7280FF" },
  chipStyle: { background: "#F9FAFBFF", borderColor: "#E5E7EBFF", borderWidth: 1, borderRadius: 3, paddingX: 6, paddingY: 1 },
};

// A dropdown of the item row's fields with a search box at the top. The menu
// is positioned "fixed" so the scrolling properties panel doesn't clip it.
function FieldSelect({ value, fields, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const matches = fields.filter((field) => field.toLowerCase().includes(query.trim().toLowerCase()));

  const toggle = (next) => {
    setOpen(next);
    if (next) {
      setQuery("");
      setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
    }
  };
  const choose = (field) => {
    onChange(field);
    setOpen(false);
  };
  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const first = menuRef.current && menuRef.current.querySelector(".dropdown-item");
      if (first) first.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (matches.length) choose(matches[0]);
    }
  };

  return (
    <Dropdown show={open} onToggle={toggle} style={{ flex: 1, minWidth: 0 }}>
      <Dropdown.Toggle
        as="button"
        type="button"
        className={"field-select-toggle form-select form-select-sm text-start text-truncate" + (value ? "" : " text-muted")}
        style={{ width: "100%" }}
        title={value || undefined}
      >
        {value || "Field"}
      </Dropdown.Toggle>
      <Dropdown.Menu
        ref={menuRef}
        className="p-1"
        style={{ minWidth: "220px", zIndex: 10800 }}
        popperConfig={{ strategy: "fixed" }}
      >
        <FormControl
          ref={searchRef}
          size="sm"
          className="mb-1"
          placeholder="Search fields"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
        />
        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
          {matches.map((field) => (
            <Dropdown.Item key={field} as="button" type="button" className="small" active={field === value} onClick={() => choose(field)}>
              {field}
            </Dropdown.Item>
          ))}
          {!matches.length && <div className="small text-muted px-2 py-1">No matching fields</div>}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

// The optional line under every item row (spec §6.2).
export default function DetailRowSettings({ detailRow, itemFields, onChange }) {
  const enabled = !!detailRow;
  const settings = { ...DETAIL_ROW_DEFAULTS, ...(detailRow || {}) };
  const update = (patch) => onChange({ ...settings, ...patch });
  const segments = settings.segments || [];
  const updateSegment = (i, patch) => update({ segments: segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  return (
    <div className="mt-2">
      <div className="form-check mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          id="detailRowEnabled"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? { ...DETAIL_ROW_DEFAULTS } : undefined)}
        />
        <label className="form-check-label small fw-bold" htmlFor="detailRowEnabled">
          Detail line under each row
        </label>
      </div>
      {enabled && (
        <>
          {segments.map((segment, i) => (
            <div key={i} className="d-flex align-items-center mb-1" style={{ gap: "4px" }}>
              <FormControl size="sm" placeholder="Label" style={{ flex: 1, minWidth: 0 }} value={segment.label || ""} onChange={(e) => updateSegment(i, { label: e.target.value })} />
              <FieldSelect value={segment.field || ""} fields={itemFields} onChange={(field) => updateSegment(i, { field })} />
              <Button size="sm" variant="outline-danger" title="Remove" onClick={() => update({ segments: segments.filter((_, j) => j !== i) })}>
                <FontAwesomeIcon icon="trash" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline-primary" className="mb-2" onClick={() => update({ segments: [...segments, { label: "", field: "" }] })}>
            <FontAwesomeIcon icon="plus" className="me-1" />
            Add field
          </Button>

          <div className="d-flex flex-wrap align-items-end mb-2" style={{ gap: "8px" }}>
            <Form.Group>
              <Form.Label className="mb-1 small">Style</Form.Label>
              <select className="form-select form-select-sm" value={settings.style} onChange={(e) => update({ style: e.target.value })}>
                <option value="chips">Boxed labels</option>
                <option value="text">Plain text</option>
              </select>
            </Form.Group>
            {settings.style === "text" && (
              <Form.Group style={{ width: "90px" }}>
                <Form.Label className="mb-1 small">Separator</Form.Label>
                <FormControl size="sm" value={settings.separator} onChange={(e) => update({ separator: e.target.value })} />
              </Form.Group>
            )}
            <Form.Group style={{ width: "80px" }}>
              <Form.Label className="mb-1 small">Size (pt)</Form.Label>
              <FormControl
                size="sm"
                type="number"
                min="5"
                value={settings.font.fontSize}
                onChange={(e) => update({ font: { ...settings.font, fontSize: parseFloat(e.target.value) || 7 } })}
              />
            </Form.Group>
          </div>
          <div className="d-flex flex-wrap align-items-end mb-2" style={{ gap: "8px" }}>
            <Form.Group className="compact-colour-field" style={{ width: "140px" }}>
              <Form.Label className="mb-1 small">Text colour</Form.Label>
              <ColorPicker color={settings.font.color} callbackFromParents={(e) => update({ font: { ...settings.font, color: e.hex } })} />
            </Form.Group>
            {settings.style === "chips" && (
              <>
                <Form.Group className="compact-colour-field" style={{ width: "140px" }}>
                  <Form.Label className="mb-1 small">Box background</Form.Label>
                  <ColorPicker color={settings.chipStyle.background} callbackFromParents={(e) => update({ chipStyle: { ...settings.chipStyle, background: e.hex } })} />
                </Form.Group>
                <Form.Group className="compact-colour-field" style={{ width: "140px" }}>
                  <Form.Label className="mb-1 small">Box border</Form.Label>
                  <ColorPicker color={settings.chipStyle.borderColor} callbackFromParents={(e) => update({ chipStyle: { ...settings.chipStyle, borderColor: e.hex } })} />
                </Form.Group>
              </>
            )}
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="detailRowHideEmpty"
              checked={settings.hideEmpty}
              onChange={(e) => update({ hideEmpty: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor="detailRowHideEmpty">Leave out fields with no value</label>
          </div>
        </>
      )}
    </div>
  );
}
